import React, { useState } from 'react';
import 'twin.macro';
import { formatCycle } from 'containers/Programmations/ProgV2/utils/formatCycle';
import Button from 'components/Buttons/Button';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import SimpleNarrowPage from 'components/Layouts/SimpleNarrowPage';
import { Infobox } from 'components/Callout/Infobox';
import { PreviewModal } from 'containers/EmploiDuTemps/New/PreviewModal';
import { Footer } from 'containers/Programmations/ProgV2/New/Footer';
import { Matiere } from '@teetsh/app/src/types/bo';
import { useFilteredProgModeles } from './useFilteredProgModeles';
import TemplateCard, { TagData } from 'components/TemplateCard';
import { orderByNiveau } from '@teetsh/app/src/utils/orderByNiveau';
import { BadgeColor } from 'types/colors';

interface Props {
  onSelect: (id: string) => void;
  matiere: Matiere;
  cycle: string;
  isProgression: boolean;
}

export function ProgModeles(props: Props) {
  const { onSelect, matiere, cycle, isProgression } = props;
  const {
    data = [],
    isLoading,
    error,
  } = useFilteredProgModeles({
    cycle: formatCycle(cycle),
    view: isProgression ? 'week' : 'periode',
    subject: matiere.nom,
  });
  const { t } = useTranslation(['common', 'programmation']);
  const [numModelsToShow, setNumModelsToShow] = useState(4); // new state variable
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);

  if (isLoading) {
    return (
      <>
        <div tw="pt-8">
          <SimpleNarrowPage>
            <div tw="space-y-4">
              <div className="tsh-skeleton" tw="h-32" />
              <div className="tsh-skeleton" tw="h-32" />
              <div className="tsh-skeleton" tw="h-32" />
            </div>
          </SimpleNarrowPage>
        </div>
        <Footer
          isProgression={isProgression}
          label={t('programmation:onboarding.startFromScratch')}
          cycle={cycle}
          matiere={matiere}
        />
      </>
    );
  }
  if (error) {
    return (
      <>
        <Infobox
          tw="mb-8"
          intent="warning"
          title={t('programmation:messages.modele.loadError')}
        />
        <Footer
          isProgression={isProgression}
          label={t('programmation:onboarding.startFromScratch')}
          cycle={cycle}
          matiere={matiere}
        />
      </>
    );
  }
  if (data.length === 0) {
    return (
      <Footer
        isProgression={isProgression}
        cycle={cycle}
        label={t('programmation:onboarding.start')}
        matiere={matiere}
      />
    );
  }
  return (
    <>
      <div>
        <div tw="text-lg font-medium mb-4 flex gap-x-2 items-center">
          {t('programmation:onboarding.XmodelesAvailable', {
            count: data.length,
          })}
        </div>
        <div tw="mb-6">
          <PreviewModal
            url={previewUrl}
            onClose={() => setPreviewUrl(undefined)}
          />
          <div tw="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {data.slice(0, numModelsToShow).map(tt => {
              const tags: TagData[] = [
                ...orderByNiveau(tt.categories, () => tt.name).map(cat => ({
                  key: cat.name,
                  label: cat.name,
                  color: cat.color,
                })),
                ...(tt.editor?.name
                  ? [
                      {
                        key: 'editor',
                        label: t('common:editorTag'),
                        color: 'indigo' as BadgeColor,
                      },
                    ]
                  : []),
              ];

              return (
                <TemplateCard
                  key={tt.documentId}
                  item={tt}
                  tags={tags}
                  onPreview={() => setPreviewUrl(tt.preview)}
                  onSelect={() => onSelect(tt.documentId)}
                  buttonText={t('programmation:onboarding.useThisModele')}
                />
              );
            })}
          </div>
        </div>
        {data.length > numModelsToShow && (
          <div tw="text-center mb-8">
            <Button
              minimal
              icon={ChevronDownIcon}
              onClick={() => setNumModelsToShow(prev => prev + 4)}
            >
              {t('programmation:onboarding.showMore')}
            </Button>
          </div>
        )}
      </div>
      <Footer
        isProgression={isProgression}
        label={t('programmation:onboarding.startFromScratch')}
        cycle={cycle}
        matiere={matiere}
      />
    </>
  );
}
